/**
 * Responsive.js
 * 
 * This file provides responsive design utilities using styled-components.
 * It defines media query breakpoints and helper functions to make
 * responsive design easier and more consistent throughout the application.
 */

import { css } from 'styled-components';

// Breakpoint sizes in pixels
export const breakpoints = {
  xs: 0,      // Extra small devices (portrait phones)
  sm: 576,    // Small devices (landscape phones)
  md: 768,    // Medium devices (tablets)
  lg: 992,    // Large devices (desktops)
  xl: 1200,   // Extra large devices (large desktops)
  xxl: 1400   // Extra extra large devices
};

/**
 * Media query functions for different screen sizes
 * Usage example:
 * 
 * const MyComponent = styled.div`
 *   font-size: 16px;
 *   
 *   ${media.down.md`
 *     font-size: 14px;
 *   `}
 *   
 *   ${media.between('sm', 'lg')`
 *     padding: 0 20px;
 *   `}
 * `;
 */
export const media = {
  // Media queries for screens up to a certain breakpoint
  down: {
    xs: (...args) => css`
      @media (max-width: ${breakpoints.xs - 0.02}px) {
        ${css(...args)}
      }
    `,
    sm: (...args) => css`
      @media (max-width: ${breakpoints.sm - 0.02}px) {
        ${css(...args)}
      }
    `,
    md: (...args) => css`
      @media (max-width: ${breakpoints.md - 0.02}px) {
        ${css(...args)}
      }
    `,
    lg: (...args) => css`
      @media (max-width: ${breakpoints.lg - 0.02}px) {
        ${css(...args)}
      }
    `,
    xl: (...args) => css`
      @media (max-width: ${breakpoints.xl - 0.02}px) {
        ${css(...args)}
      }
    `,
    xxl: (...args) => css`
      @media (max-width: ${breakpoints.xxl - 0.02}px) {
        ${css(...args)}
      }
    `
  },
  
  // Media queries for screens at least a certain breakpoint
  up: {
    xs: (...args) => css`
      @media (min-width: ${breakpoints.xs}px) {
        ${css(...args)}
      }
    `,
    sm: (...args) => css`
      @media (min-width: ${breakpoints.sm}px) {
        ${css(...args)}
      }
    `,
    md: (...args) => css`
      @media (min-width: ${breakpoints.md}px) {
        ${css(...args)}
      }
    `,
    lg: (...args) => css`
      @media (min-width: ${breakpoints.lg}px) {
        ${css(...args)}
      }
    `,
    xl: (...args) => css`
      @media (min-width: ${breakpoints.xl}px) {
        ${css(...args)}
      }
    `,
    xxl: (...args) => css`
      @media (min-width: ${breakpoints.xxl}px) {
        ${css(...args)}
      }
    `
  },
  
  // Media queries for screens between two breakpoints
  between: (lower, upper) => (...args) => css`
    @media (min-width: ${breakpoints[lower]}px) and (max-width: ${breakpoints[upper] - 0.02}px) {
      ${css(...args)}
    }
  `,
  
  // Media query for specific device orientations
  orientation: {
    portrait: (...args) => css`
      @media (orientation: portrait) {
        ${css(...args)}
      }
    `,
    landscape: (...args) => css`
      @media (orientation: landscape) {
        ${css(...args)}
      }
    `
  },
  
  // Media query for retina/high DPI displays
  retina: (...args) => css`
    @media 
      (-webkit-min-device-pixel-ratio: 2),
      (min-resolution: 192dpi) {
        ${css(...args)}
    }
  `
};

/**
 * Container widths for different breakpoints
 * These match Bootstrap's container widths for consistency
 */
export const containerWidths = {
  sm: '540px',
  md: '720px',
  lg: '960px',
  xl: '1140px',
  xxl: '1320px'
};

/**
 * Responsive font size utility
 * Scales font size based on viewport width
 * 
 * @param {number} minSize - Minimum font size in pixels
 * @param {number} maxSize - Maximum font size in pixels
 * @param {number} minWidth - Viewport width at which minSize applies
 * @param {number} maxWidth - Viewport width at which maxSize applies
 * @returns {string} CSS for responsive font size
 */
export const responsiveFontSize = (minSize, maxSize, minWidth = breakpoints.sm, maxWidth = breakpoints.xl) => css`
  font-size: ${minSize}px;
  
  @media (min-width: ${minWidth}px) {
    font-size: calc(${minSize}px + (${maxSize} - ${minSize}) * ((100vw - ${minWidth}px) / (${maxWidth} - ${minWidth})));
  }
  
  @media (min-width: ${maxWidth}px) {
    font-size: ${maxSize}px;
  }
`;

/**
 * Helper for creating responsive grid layouts
 * 
 * @param {number} columns - Number of columns on desktop
 * @param {number} tabletColumns - Number of columns on tablet
 * @param {number} mobileColumns - Number of columns on mobile
 * @param {string} gap - Gap between grid items
 * @returns {string} CSS for responsive grid
 */
export const responsiveGrid = (columns = 3, tabletColumns = 2, mobileColumns = 1, gap = '20px') => css`
  display: grid;
  grid-template-columns: repeat(${columns}, 1fr);
  gap: ${gap};
  
  ${media.down.lg`
    grid-template-columns: repeat(${tabletColumns}, 1fr);
  `}
  
  ${media.down.sm`
    grid-template-columns: repeat(${mobileColumns}, 1fr);
  `}
`;

export default media;
